// Admin → Costs (spec-v2.1.md, "Admin cost panel"): for today, 7 days or 30 days, what the providers cost us, what
// users were charged, and the margin; then the same by kind of work with the markup (under 1.5× is red), top users
// by spend, and which rates are still unverified. Admins only. v3 adds the Margins section (MarginsSection below).
import { useMemo, useState, type CSSProperties } from "react";
import { api, call } from "../api";
import { Empty, ErrorNote, Spinner, useLoad } from "../components/bits";
import { Shell } from "../components/Shell";
import { Link } from "../router";
import { useStore } from "../store";
import type { CostKind, CostWindow } from "../types";

const WINDOWS: { id: CostWindow; label: string }[] = [
  { id: "1d", label: "Today" },
  { id: "7d", label: "7 days" },
  { id: "30d", label: "30 days" },
];
const KIND: Record<CostKind, string> = { frame: "Frames", clip: "Clips", voice: "Voice", music: "Music", sfx: "Sound effects", cues: "Cue detection", render: "Renders", script: "Scripts", director: "Director minutes", other: "Other" };
/** Below this markup the row is red. */
export const LOW_MARKUP = 1.5;

const usd = (n: number) => n.toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 });
const x = (m: number | null) => (m == null ? "Not charged" : `${m.toFixed(2)}×`);
export const isLow = (m: number | null, spend: number) => spend > 0 && (m == null || m < LOW_MARKUP);

export function AdminCostsScreen() {
  const me = useStore((s) => s.me);
  const [win, setWin] = useState<CostWindow>("7d");
  const { data, error, loading, reload } = useLoad(() => api.adminCosts(win), [win]);
  if (me && me.user.role !== "admin")
    return (
      <Shell>
        <div className="page">
          <Empty title="Admins only" action={<Link to="/" className="btn btn-quiet">Go home</Link>}>
            This page shows what the studio costs to run.
          </Empty>
        </div>
      </Shell>
    );
  const unverified = data?.byKind.filter((r) => r.unverified) ?? [];
  const ooc = data?.providerOutOfCredit;
  const oocRecent = ooc && Date.now() - Date.parse(ooc.at) < 24 * 3600_000 ? ooc : null;
  return (
    <Shell>
      <div className="page costs">
        <header className="page-head costs-head">
          <div>
            <h1 className="page-title">Costs</h1>
            <p className="page-lede">Provider spend against what users were charged.</p>
          </div>
          <span className="seg" role="radiogroup" aria-label="Window">
            {WINDOWS.map((w) => (
              <button key={w.id} type="button" role="radio" aria-checked={w.id === win} className={w.id === win ? "is-on" : ""} onClick={() => setWin(w.id)}>
                {w.label}
              </button>
            ))}
          </span>
        </header>
        {error && <ErrorNote text={`Couldn't load the costs. ${error}`} retry={reload} />}
        {loading && !data && <Spinner label="Adding it up" />}
        {oocRecent && (
          <div className="banner banner-warn" role="alert">
            <p>
              <strong>{providerName(oocRecent.provider)} is out of credit.</strong> Since {new Date(oocRecent.at).toLocaleString()}, work that needs it
              fails and is refunded
              {oocRecent.remaining !== null ? ` (${oocRecent.remaining.toLocaleString()} credits left on the key${oocRecent.required !== null ? `, ${oocRecent.required.toLocaleString()} needed` : ""})` : ""}. Top up the account or raise the API key's limit.
            </p>
          </div>
        )}
        {data && (
          <div className={loading ? "is-dim" : ""}>
            <dl className="costs-big">
              <div>
                <dt>Provider spend</dt>
                <dd>{usd(data.spendUsd)}</dd>
              </div>
              <div>
                <dt>Charged to users</dt>
                <dd>{usd(data.chargedUsd)}</dd>
              </div>
              <div className={isLow(data.markup, data.spendUsd) ? "is-low" : ""}>
                <dt>Margin</dt>
                <dd>{usd(data.marginUsd)}</dd>
                <p className="costs-markup">{x(data.markup)} markup</p>
              </div>
            </dl>

            <section className="costs-sec" aria-labelledby="kind-h">
              <h2 id="kind-h" className="group-title">
                By kind of work
              </h2>
              <div className="table-wrap">
                <table className="costs-table">
                  <thead>
                    <tr>
                      <th scope="col">Kind</th>
                      <th scope="col" className="num">
                        Jobs
                      </th>
                      <th scope="col" className="num">
                        Cost
                      </th>
                      <th scope="col" className="num">
                        Charged
                      </th>
                      <th scope="col" className="num">
                        Markup
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.byKind.map((r) => (
                      <tr key={r.kind} className={isLow(r.markup, r.spendUsd) ? "is-low" : ""}>
                        <th scope="row">
                          {KIND[r.kind]}
                          {r.unverified && <span className="tag tag-warn">Unverified rate</span>}
                        </th>
                        <td className="num">{r.count.toLocaleString("en-US")}</td>
                        <td className="num">{usd(r.spendUsd)}</td>
                        <td className="num">{usd(r.chargedUsd)}</td>
                        <td className="num costs-x">{x(r.markup)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="muted costs-note">Red rows earn under {LOW_MARKUP}× what they cost. Director minutes aren't charged yet, so they show what the voice line costs us.</p>
            </section>

            <div className="costs-split">
              <section className="costs-sec" aria-labelledby="users-h">
                <h2 id="users-h" className="group-title">
                  Top users by spend
                </h2>
                {data.topUsers.length ? (
                  <ol className="costs-users">
                    {data.topUsers.map((u) => (
                      <li key={u.userId}>
                        <Link to={`/admin/people/${u.userId}`} className="costs-email">
                          {u.email}
                        </Link>
                        <span className="num">{usd(u.spendUsd)}</span>
                        <span className="num muted">charged {usd(u.chargedUsd)}</span>
                      </li>
                    ))}
                  </ol>
                ) : (
                  <p className="muted">No spend in this window.</p>
                )}
              </section>
              <section className="costs-sec" aria-labelledby="unv-h">
                <h2 id="unv-h" className="group-title">
                  Unverified rates
                </h2>
                {unverified.length ? (
                  <ul className="costs-unv">
                    {unverified.map((r) => (
                      <li key={r.kind}>
                        <strong>{KIND[r.kind]}</strong> uses a model whose price isn't confirmed against the provider's bill yet. Check it before relying on the margin.
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="muted">Every rate in this window is checked against the provider's pricing.</p>
                )}
              </section>
            </div>
          </div>
        )}
        <MarginsSection />
      </div>
    </Shell>
  );
}

// ------------------------------------------------------------------ Margins (v3, workstream F)
// GET /api/admin/margins: every model and fixed price, what one unit costs us, what we charge, and the multiple on
// the pack that pays least per credit after Stripe (worst) and the one that pays most (best), with a proposed price
// at the target. Prices don't change from here: the proposal is docs/product/pricing-v3-proposal.md.
// The fetch lives here (not in src/api.ts) so this workstream stays in its own file; under VITE_MOCK a small fixture
// stands in.

export type MarginStatus = "healthy" | "thin" | "losing";
export type MarginActual = { count: number; failed: number; totalUsd: number; units: number; usdPerUnit: number | null; worstMultiple: number | null };
export type MarginRowView = {
  id: string;
  label: string;
  kind: "model" | "fixed";
  step: string;
  unit: string;
  providerUsdPerUnit: number;
  creditsPerUnit: number;
  charged: boolean;
  listedCredits: number | null;
  worstMultiple: number | null;
  bestMultiple: number | null;
  status: MarginStatus;
  belowTarget: boolean;
  proposedCredits: number;
  proposedWorstMultiple: number | null;
  change: "raise" | "keep" | "could_lower";
  rateSource: "documented" | "unverified";
  note: string | null;
  actual: MarginActual | null;
};
export type PackView = { id: string; name: string; credits: number; priceUsd: number; feeShare: number; netUsdPerCredit: number };
export type MarginsView = {
  target: number;
  losingBelow: number;
  windowDays: number;
  packs: PackView[];
  worstPack: PackView | null;
  bestPack: PackView | null;
  rows: MarginRowView[];
  summary: { rows: number; belowTarget: number; healthy: number; thin: number; losing: number };
};

type O = Record<string, unknown>;
const isO = (v: unknown): v is O => !!v && typeof v === "object" && !Array.isArray(v);
const n = (v: unknown, d = 0) => (typeof v === "number" && Number.isFinite(v) ? v : typeof v === "string" && v.trim() && Number.isFinite(Number(v)) ? Number(v) : d);
const nn = (v: unknown) => (v === null || v === undefined ? null : n(v, NaN)) as number | null;
const s = (v: unknown, d = "") => (typeof v === "string" ? v : d);
const status = (v: unknown): MarginStatus => (v === "thin" || v === "losing" ? v : "healthy");

function normPackView(v: unknown): PackView {
  const o = isO(v) ? v : {};
  return { id: s(o.id), name: s(o.name, s(o.id)), credits: n(o.credits), priceUsd: n(o.priceUsd), feeShare: n(o.feeShare), netUsdPerCredit: n(o.netUsdPerCredit) };
}

/** Tolerant reading of GET /api/admin/margins. */
export function normMargins(v: unknown): MarginsView {
  const o = isO(v) ? v : {};
  const rows = (Array.isArray(o.rows) ? o.rows : []).filter(isO).map((r): MarginRowView => {
    const a = isO(r.actual) ? r.actual : null;
    const m = (x: unknown) => {
      const y = nn(x);
      return y === null || Number.isNaN(y) ? null : y;
    };
    return {
      id: s(r.id),
      label: s(r.label, s(r.id)),
      kind: r.kind === "fixed" ? "fixed" : "model",
      step: s(r.step),
      unit: s(r.unit, "unit"),
      providerUsdPerUnit: n(r.providerUsdPerUnit),
      creditsPerUnit: n(r.creditsPerUnit),
      charged: r.charged !== false,
      listedCredits: m(r.listedCredits),
      worstMultiple: m(r.worstMultiple),
      bestMultiple: m(r.bestMultiple),
      status: status(r.status),
      belowTarget: r.belowTarget === true,
      proposedCredits: n(r.proposedCredits, 1),
      proposedWorstMultiple: m(r.proposedWorstMultiple),
      change: r.change === "raise" || r.change === "could_lower" ? r.change : "keep",
      rateSource: r.rateSource === "unverified" ? "unverified" : "documented",
      note: typeof r.note === "string" ? r.note : null,
      actual: a ? { count: n(a.count), failed: n(a.failed), totalUsd: n(a.totalUsd), units: n(a.units), usdPerUnit: m(a.usdPerUnit), worstMultiple: m(a.worstMultiple) } : null,
    };
  });
  const sum = isO(o.summary) ? o.summary : {};
  const count = (st: MarginStatus) => rows.filter((r) => r.status === st).length;
  return {
    target: n(o.target, 2),
    losingBelow: n(o.losingBelow, 1.2),
    windowDays: n(o.windowDays, 30),
    packs: (Array.isArray(o.packs) ? o.packs : []).map(normPackView),
    worstPack: isO(o.worstPack) ? normPackView(o.worstPack) : null,
    bestPack: isO(o.bestPack) ? normPackView(o.bestPack) : null,
    rows,
    summary: {
      rows: n(sum.rows, rows.length),
      belowTarget: n(sum.belowTarget, rows.filter((r) => r.belowTarget).length),
      healthy: n(sum.healthy, count("healthy")),
      thin: n(sum.thin, count("thin")),
      losing: n(sum.losing, count("losing")),
    },
  };
}

export type MarginSort = "item" | "cost" | "charge" | "worst" | "best" | "status" | "proposed";
const STATUS_ORDER: Record<MarginStatus, number> = { losing: 0, thin: 1, healthy: 2 };

/** Sorted copy; ties fall back to the worst multiple, then the label. Nulls (nothing to compare) sort last. */
export function sortMargins(rows: MarginRowView[], by: MarginSort, dir: 1 | -1): MarginRowView[] {
  const key = (r: MarginRowView): number | string | null => {
    switch (by) {
      case "item":
        return r.label.toLowerCase();
      case "cost":
        return r.providerUsdPerUnit;
      case "charge":
        return r.creditsPerUnit;
      case "worst":
        return r.worstMultiple;
      case "best":
        return r.bestMultiple;
      case "status":
        return STATUS_ORDER[r.status];
      case "proposed":
        return r.proposedCredits - r.creditsPerUnit;
    }
  };
  const cmp = (a: number | string | null, b: number | string | null) => {
    if (a === null && b === null) return 0;
    if (a === null) return 1;
    if (b === null) return -1;
    return typeof a === "string" ? a.localeCompare(String(b)) * dir : ((a as number) - (b as number)) * dir;
  };
  return [...rows].sort((a, b) => cmp(key(a), key(b)) || cmp(a.worstMultiple, b.worstMultiple) || a.label.localeCompare(b.label));
}

const MOCK_MARGINS = {
  target: 2,
  losingBelow: 1.2,
  windowDays: 30,
  packs: [
    { id: "creator", name: "Creator", credits: 2900, priceUsd: 29, feeShare: 0.0393, netUsdPerCredit: 0.009607 },
    { id: "pro", name: "Pro", credits: 11000, priceUsd: 99, feeShare: 0.032, netUsdPerCredit: 0.008712 },
  ],
  worstPack: { id: "pro", name: "Pro", credits: 11000, priceUsd: 99, feeShare: 0.032, netUsdPerCredit: 0.008712 },
  bestPack: { id: "creator", name: "Creator", credits: 2900, priceUsd: 29, feeShare: 0.0393, netUsdPerCredit: 0.009607 },
  rows: [
    { id: "runway:gen4_turbo", label: "Runway Gen-4 Turbo", kind: "model", step: "clip", unit: "second", providerUsdPerUnit: 0.05, creditsPerUnit: 10, worstMultiple: 1.74, bestMultiple: 1.92, status: "thin", belowTarget: true, proposedCredits: 12, proposedWorstMultiple: 2.09, change: "raise", rateSource: "documented", actual: { count: 4, failed: 0, totalUsd: 1.25, units: 25, usdPerUnit: 0.05, worstMultiple: 1.74 } },
    { id: "gemini:gemini-nano-banana-2.1", label: "Nano Banana 2.1", kind: "model", step: "image", unit: "image", providerUsdPerUnit: 0.0504, creditsPerUnit: 15, worstMultiple: 2.59, bestMultiple: 2.86, status: "healthy", belowTarget: false, proposedCredits: 12, proposedWorstMultiple: 2.07, change: "could_lower", rateSource: "documented", actual: null },
    { id: "fixed:ambience", label: "Ambience bed (10 s loop)", kind: "fixed", step: "sfx", unit: "effect", providerUsdPerUnit: 0.04, creditsPerUnit: 5, worstMultiple: 1.09, bestMultiple: 1.2, status: "losing", belowTarget: true, proposedCredits: 10, proposedWorstMultiple: 2.18, change: "raise", rateSource: "unverified", actual: null },
    { id: "fixed:director", label: "Director minute", kind: "fixed", step: "director", unit: "minute", providerUsdPerUnit: 0.1, creditsPerUnit: 0, charged: false, listedCredits: 25, worstMultiple: 0, bestMultiple: 0, status: "losing", belowTarget: true, proposedCredits: 25, proposedWorstMultiple: 2.18, change: "raise", rateSource: "unverified", note: "not debited today", actual: { count: 12, failed: 0, totalUsd: 3.2, units: 40, usdPerUnit: 0.08, worstMultiple: 0 } },
  ],
};

async function loadMargins(): Promise<MarginsView> {
  if (import.meta.env.VITE_MOCK === "1") return normMargins(MOCK_MARGINS);
  return normMargins(await call("GET", "admin/margins"));
}

const CHIP: Record<MarginStatus, { label: string; style: CSSProperties }> = {
  healthy: { label: "Healthy", style: { color: "var(--good)", borderColor: "rgb(138 214 170 / 0.4)" } },
  thin: { label: "Thin", style: { color: "var(--tungsten-hi)", borderColor: "rgb(255 178 62 / 0.4)" } },
  losing: { label: "Losing", style: { color: "var(--bad)", borderColor: "rgb(255 138 120 / 0.45)" } },
};
const money = (v: number) => (v >= 1 ? `$${v.toFixed(2)}` : v >= 0.01 ? `$${v.toFixed(3)}` : `$${v.toFixed(4)}`);
const mult = (m: number | null) => (m === null ? "–" : `${m.toFixed(2)}×`);
const perUnit = (u: string) => (u === "1k characters" ? "1k chars" : u);
const sortBtn: CSSProperties = { all: "unset", cursor: "pointer", display: "inline-flex", gap: 4, alignItems: "center" };

function MarginsSection() {
  const { data, error, loading, reload } = useLoad(loadMargins, []);
  const [sort, setSort] = useState<{ by: MarginSort; dir: 1 | -1 }>({ by: "worst", dir: 1 });
  const rows = useMemo(() => (data ? sortMargins(data.rows, sort.by, sort.dir) : []), [data, sort]);
  const head = (by: MarginSort, label: string, num = true) => {
    const on = sort.by === by;
    return (
      <th scope="col" className={num ? "num" : undefined} aria-sort={on ? (sort.dir === 1 ? "ascending" : "descending") : "none"}>
        <button type="button" style={sortBtn} onClick={() => setSort((s0) => ({ by, dir: s0.by === by ? ((-s0.dir) as 1 | -1) : 1 }))}>
          {label}
          <span aria-hidden style={{ opacity: on ? 1 : 0.3 }}>{on && sort.dir === -1 ? "↓" : "↑"}</span>
        </button>
      </th>
    );
  };
  const worstName = data?.worstPack?.name ?? "the worst pack";
  return (
    <section className="costs-sec" aria-labelledby="margins-h">
      <h2 id="margins-h" className="group-title">
        Margins
      </h2>
      {error && <ErrorNote text={`Couldn't load the margins. ${error}`} retry={reload} />}
      {loading && !data && <Spinner label="Working out the margins" />}
      {data && (
        <>
          <p className={data.summary.belowTarget ? "" : "muted"} style={data.summary.belowTarget ? { color: "var(--bad)", fontWeight: 600 } : undefined}>
            {data.summary.belowTarget
              ? `${data.summary.belowTarget} of ${data.summary.rows} items earn under ${data.target.toFixed(1)}× on ${worstName}`
              : `Every item earns at least ${data.target.toFixed(1)}× on ${worstName}`}
            <span className="muted" style={{ fontWeight: 400 }}>
              {" "}
              · {data.summary.losing} losing, {data.summary.thin} thin, {data.summary.healthy} healthy
            </span>
          </p>
          {data.packs.length > 0 && (
            <p className="muted costs-note">
              After Stripe, a credit pays us{" "}
              {data.packs.map((p, i) => (
                <span key={p.id}>
                  {i ? ", " : ""}
                  {money(p.netUsdPerCredit)} on {p.name}
                </span>
              ))}
              .
            </p>
          )}
          {/* eight columns don't fit a phone: the table keeps its width and scrolls inside its frame */}
          <div className="table-wrap">
            <table className="costs-table" style={{ minWidth: 760 }}>
              <thead>
                <tr>
                  {head("item", "Model or item", false)}
                  <th scope="col">Unit</th>
                  {head("cost", "Our cost")}
                  {head("charge", "We charge")}
                  {head("worst", "Worst")}
                  {head("best", "Best")}
                  {head("status", "Status", false)}
                  {head("proposed", "Proposed")}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className={r.status === "losing" ? "is-low" : ""}>
                    <th scope="row" style={{ minWidth: 170 }}>
                      {r.label}
                      {r.rateSource === "unverified" && <span className="tag tag-warn">Estimate</span>}
                      {r.note && (
                        <span className="muted" style={{ display: "block", fontSize: 12, marginTop: 2 }}>
                          {r.note}
                        </span>
                      )}
                    </th>
                    <td>{perUnit(r.unit)}</td>
                    <td className="num">
                      {money(r.providerUsdPerUnit)}
                      {r.actual && (
                        <span className="muted" style={{ display: "block", fontSize: 12 }} title={`${r.actual.count} spend lines in ${data.windowDays} days, $${r.actual.totalUsd.toFixed(2)} in all`}>
                          {r.actual.usdPerUnit === null ? "actual: per unit unknown" : `actual ${money(r.actual.usdPerUnit)}`}
                        </span>
                      )}
                    </td>
                    <td className="num">{r.charged ? r.creditsPerUnit : <span title={r.listedCredits !== null ? `listed at ${r.listedCredits} credits` : undefined}>Not charged</span>}</td>
                    <td className="num costs-x">{mult(r.worstMultiple)}</td>
                    <td className="num">{mult(r.bestMultiple)}</td>
                    <td>
                      <span className="tag" style={CHIP[r.status].style}>
                        {CHIP[r.status].label}
                      </span>
                    </td>
                    <td className="num" title={r.proposedWorstMultiple !== null ? `${mult(r.proposedWorstMultiple)} on ${worstName}` : undefined}>
                      {r.change === "keep" ? (
                        <span className="muted">{r.proposedCredits}</span>
                      ) : (
                        <strong style={r.change === "raise" ? { color: "var(--tungsten-hi)" } : { fontWeight: 500 }}>
                          {r.change === "raise" ? "↑ " : "↓ "}
                          {r.proposedCredits}
                        </strong>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="muted costs-note">
            Multiple = the credits we charge for one unit, at what a credit from that pack pays us after Stripe (2.9% + $0.30), over what the provider charges us. Worst is {worstName}, the pack that pays least per credit. Healthy is {data.target.toFixed(1)}× or more, thin {data.losingBelow.toFixed(1)}–{data.target.toFixed(1)}×, losing under {data.losingBelow.toFixed(1)}×. Proposed is the smallest clean price that reaches {data.target.toFixed(1)}× on the worst pack (↑ raise, ↓ could come down). Actual is the last {data.windowDays} days of spend per unit. Nothing changes until the proposal is approved.
          </p>
        </>
      )}
    </section>
  );
}

const PROVIDER_NAMES: Record<string, string> = { elevenlabs: "ElevenLabs", fal: "fal", runway: "Runway", gemini: "Gemini", google: "Google" };
function providerName(p: string) {
  return PROVIDER_NAMES[p] ?? p;
}
